import React from 'react';

import {
  DateField,
  Edit,
  TextField,
  usePermissions,
  TabbedShowLayout,
  Labeled,
  ListBase,
  useRecordContext,
} from 'react-admin';

import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  SxProps,
} from '@mui/material';

import useCustomEditView from '../../hooks/useCustomEditView';

import actions, { Actions } from '../../constants/actions';

import hasAccess from '../../utilities/hasAccess';

import IndividualBreadcrumb from './ProductBreadcrumb';

import KycForm from '../kycForm';

import useValidateId from '../../hooks/useValidateId';

import resourceTypes from '../../constants/resourceTypes';
import { entityCleaner } from '../../utilities/dataProvider/cleaner';
import EditArrayInput from '../../customFields/EditArrayInput';
import RiskManagement from '../riskManagment';
import RelatedCustomConnections from '../connection/RelatedCustomConnections';
import LatestRelatedRequests from '../../customFields/LatestRelatedRequests';
import LatestRelatedCases from '../../customFields/LatestRelatedCases';
import LatestRelatedPings from '../../customFields/LatestRelatedPings';
import EditField from '../../customFields/EditField';
import EditSelectInput from '../../customFields/EditSelectInput';
import { productTypesChoices } from '../../constants/productTypes';
import ProductParties from '../../customFields/ProductParties';
import ProductInformationField from './productInformation/fields';
import Transaction from '../transaction';
import reportTypes from '../../constants/reportTypes';
import ReportModal from '../../customFields/ReportModal';
import { attributesSchema, checkIsTypeAllowed } from '../../utilities/reports/permissions';
import AuditResourceList from '../audit/AuditResourceList';
import EditToolbar from '../layout/EditToolbar';
import { ReportListDatagrid } from '../reports/ReportHistoryTable';

const customEditSx = (isCustomView: boolean): SxProps => (
  isCustomView
    ? { '& .RaEdit-card': { backgroundColor: 'transparent', boxShadow: 'none', marginTop: 0 } }
    : { '& .RaEdit-card': { marginTop: 0 } }
);

const EditActions = () => (
  <EditToolbar>
    <ReportModal />
  </EditToolbar>
);

export const ProductsEditContent = () => {
  const { isCustomView } = useCustomEditView();
  const record = useRecordContext();

  const { permissions } = usePermissions<Actions[]>();

  if (!record) return null;

  return (
    <TabbedShowLayout
      sx={isCustomView ? { display: 'none' } : {}}
    >
      <TabbedShowLayout.Tab label="Information">
        <Labeled label={false} sx={{ display: 'flex' }}>
          <Grid container spacing={6}>
            <Grid
              size={{
                xs: 12,
                lg: 5,
              }}
            >
              <Card variant="outlined">
                <CardHeader
                  title="Product details"
                />
                <Divider />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid size={12}>
                      <Labeled label="ID">
                        <TextField source="id" label="ID" />
                      </Labeled>
                    </Grid>
                    <Grid size={12}>
                      <Labeled label="Created at">
                        <DateField source="createdAt" />
                      </Labeled>
                    </Grid>
                    <Grid size={12}>
                      <EditArrayInput showEdit={hasAccess(permissions, actions.PRODUCT_UPDATE)} source="customIds" label="Custom IDs" />
                    </Grid>
                    <Grid size={12}>
                      <EditField showEdit={hasAccess(permissions, actions.PRODUCT_UPDATE)} source="productNumber" label="Product number" />
                    </Grid>
                    <Grid size={12}>
                      <EditField showEdit={hasAccess(permissions, actions.PRODUCT_UPDATE)} source="productDescription" label="Product description" />
                    </Grid>
                    <Grid size={12}>
                      <EditSelectInput
                        showEdit={hasAccess(permissions, actions.PRODUCT_UPDATE)}
                        source="productType"
                        label="Product type"
                        choices={productTypesChoices}
                      />
                    </Grid>
                    <ProductInformationField
                      showEdit={hasAccess(permissions, actions.PRODUCT_UPDATE)}
                    />
                    <Grid size={12}>
                      <ProductParties showEdit={hasAccess(permissions, actions.PRODUCT_UPDATE)} />
                    </Grid>
                    <Grid size={12}>
                      <EditField showEdit={hasAccess(permissions, actions.PRODUCT_UPDATE)} type="date" source="kycApprovedAt" label="KYC approved" />
                    </Grid>
                    <Grid size={12}>
                      <RiskManagement />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid
              size={{
                xs: 12,
                lg: 7,
              }}
            >
              <RelatedCustomConnections />
            </Grid>
            <LatestRelatedRequests />
            <LatestRelatedCases />
            <LatestRelatedPings />
          </Grid>
        </Labeled>
      </TabbedShowLayout.Tab>
      {hasAccess(permissions, actions.FORM_ANSWER_GET) && (
        <TabbedShowLayout.Tab label="KYC form" path="form-answers/*">
          <KycForm />
        </TabbedShowLayout.Tab>
      )}
      {hasAccess(permissions, actions.REQUEST_GET) && (
        <TabbedShowLayout.Tab label="Transactions" path="transactions">
          <Transaction />
        </TabbedShowLayout.Tab>
      )}
      {hasAccess(permissions, actions.AUDIT_TRACK_LIST) && (
        <TabbedShowLayout.Tab label="Audit trail" path="audit-tracks">
          <AuditResourceList resourceType={resourceTypes.PRODUCT} />
        </TabbedShowLayout.Tab>
      )}
      {hasAccess(permissions, actions.REPORT_LIST, {
        schema: attributesSchema,
        check: checkIsTypeAllowed(reportTypes.ENTITY_REPORT),
      }) && (
        <TabbedShowLayout.Tab label="Reports" path="reports">
          <ListBase
            storeKey={false}
            disableSyncWithLocation
            perPage={5}
            resource="reports"
            sort={{ field: 'createdAt', order: 'DESC' }}
            filter={{ type: reportTypes.ENTITY_REPORT, entityId: record.id }}
            queryOptions={{ refetchInterval: 5000 }}
          >
            <Card variant="outlined">
              <ReportListDatagrid />
            </Card>
          </ListBase>
        </TabbedShowLayout.Tab>
      )}
    </TabbedShowLayout>
  );
};

const ProductsEdit = () => {
  const { isCustomView } = useCustomEditView();

  const validatedId = useValidateId();
  if (!validatedId.isValid) return null;

  return (
    <Edit
      mutationMode="pessimistic"
      redirect={false}
      sx={customEditSx(isCustomView)}
      transform={entityCleaner}
      actions={<EditActions />}
    >
      <IndividualBreadcrumb />
      <ProductsEditContent />
    </Edit>
  );
};

export default ProductsEdit;
